import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { motion } from "framer-motion";
import ReactHtmlParser from "react-html-parser";
import jsonData from "./sectionDescription.json";
import "./Lesson.css";
import "./lessonNotes/LessonNotes.css";
import Example from "./Example";
// import exampleDatafromBackEnd from "./example.json";
import LessonExample from "./LessonExample";
import { useAmazonContext } from "../Contexts/AmazonContext";
import axios from "axios";
import api from "./api";
import LessonMainSideNav from "./sidebar/LessonMainSideNav";
import LottieAnimation from "./LottieAnimation";
import { Container } from "react-bootstrap";
import styled, { keyframes } from "styled-components";
import { useHistory } from "react-router-dom";
import HighlightableSection from "./lessonNotes/HighlightableSection";
import NoteComposer from "./lessonNotes/NoteComposer";
import LessonNotesPanel from "./lessonNotes/LessonNotesPanel";
import ShareSectionDrawer, {
  buildGrammarShareLabels,
  shareTextOnly,
} from "./lessonNotes/ShareSectionDrawer";
import LessonShareInfographic, {
  sectionHasShareableContent,
} from "./lessonNotes/LessonShareInfographic";
import { ChevronLeft, ChevronRight, Share2 } from "lucide-react";
import {
  pickRandomShareButtonLabel,
  SHARE_BUTTON_LABEL_SIZER,
} from "./lessonNotes/shareReminders";
import { capturePosterPng } from "./lessonNotes/inlinePosterImages";
import {
  findNote,
  getAnimationSectionId,
  scrollToAnimationSection,
  sortNotesByLessonOrder,
} from "./lessonNotes/noteUtils";
import { useCourseDetails } from "./hooks/useCourseDetails";
import { normalizeMeQuranArabic } from "./normalizeMeQuranArabic";

// Without a timeout a dropped connection never settles and the lesson stays on Loading.
const LESSON_REQUEST_TIMEOUT = 15000;

const LessonLoadError = styled.div`
  font-family: "Quicksand", sans-serif;
  max-width: 420px;
  margin: 20vh auto 0;
  padding: 0 24px;
  text-align: center;
  color: ${(props) => (props.$isDarkMode ? "#f8f6f0" : "#252525")};

  p {
    font-size: 1.1rem;
    margin-bottom: 24px;
  }

  .lessonLoadErrorActions {
    display: flex;
    gap: 12px;
    justify-content: center;
    flex-wrap: wrap;
  }

  button {
    font-family: inherit;
    font-weight: bold;
    text-transform: uppercase;
    padding: 0.75rem 1.5rem;
    border: 0;
    border-radius: 12px;
    cursor: pointer;
    color: white;
    background: #967bb6;
  }

  button.secondary {
    background: transparent;
    color: inherit;
    border: 2px solid #967bb6;
  }
`;

const LessonSolidButton = styled.button`
  font-family: "Quicksand", sans-serif;
  box-sizing: border-box;
  padding: ${(props) =>
    props.$variant === "continue" ? "1.35rem 2rem 1rem" : "1rem 2rem"};
  font-size: 1rem;
  flex: none;
  width: 360px;
  min-width: 360px;
  max-width: 360px;
  border: 0;
  border-radius: 16px;
  font-weight: bold;
  text-transform: uppercase;
  color: white;
  transition: transform 0.01s ease;
  cursor: pointer;
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden;
  box-shadow: 0 4px 0 ${(props) => props.shadowColor};
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:hover {
    filter: brightness(110%);
  }

  &:active {
    transform: translateX(-50%) translateY(4px);
    box-shadow: none;
  }

  @media (max-width: 768px) {
    width: 90vw;
    min-width: 90vw;
    max-width: 90vw;
    font-size: 1.2rem;
    padding: ${(props) =>
      props.$variant === "continue"
        ? "1.2rem 1.6rem 0.8rem"
        : "0.8rem 1.6rem"};
  }
`;

const ContinueProgressTrack = styled.div`
  position: absolute;
  top: 8px;
  left: 12px;
  right: 12px;
  height: 6px;
  background: rgba(255, 255, 255, 0.45);
  border-radius: 20px;
  overflow: hidden;
`;

const ContinueProgressFill = styled(motion.div)`
  height: 100%;
  width: 100%;
  transform-origin: left center;
  background: #ffffff;
  border-radius: inherit;
`;

const CompletedStickyBar = styled.div`
  display: flex;
  gap: 10px;
  box-sizing: border-box;
  background: transparent;

  ${(props) =>
    props.$sticky === false
      ? `
    position: relative;
    width: 90%;
    margin: 0 auto;
    padding: 12px 0 8px;
  `
      : `
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1000;
    padding: 12px 16px calc(16px + env(safe-area-inset-bottom, 0px));
  `}
`;

const NavLessonButton = styled.button`
  flex: 1;
  min-width: 0;
  min-height: 58px;
  font-family: "Quicksand", sans-serif;
  padding: 0.7rem 0.8rem;
  border: 0;
  border-radius: 16px;
  font-weight: bold;
  color: white;
  cursor: pointer;
  position: relative;
  top: 0;
  background: #8747d4;
  box-shadow: 0 4px 0 #6535a3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: top 0.01s ease;

  &:hover:not(:disabled) {
    background: #9859e0;
  }

  &:active:not(:disabled) {
    top: 4px;
    box-shadow: none;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const NavArrowButton = styled(NavLessonButton)`
  flex: 0 0 58px;
  width: 58px;
  padding: 0;
`;

const shareShine = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const ShareLessonButton = styled(NavLessonButton)`
  flex-direction: row;
  gap: 8px;
  color: #4a3600;
  background: linear-gradient(135deg, #ffd700 0%, #fff6a8 50%, #ffd700 100%);
  background-size: 200% auto;
  animation: ${shareShine} 3s linear infinite;
  box-shadow: 0 4px 0 #b8860b, 0 4px 16px rgba(255, 215, 0, 0.45);
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #ffe44d 0%, #fffbe0 50%, #ffe44d 100%);
    background-size: 200% auto;
    box-shadow: 0 4px 0 #b8860b, 0 6px 20px rgba(255, 215, 0, 0.6);
  }

  &:active:not(:disabled) {
    box-shadow: none;
  }
`;

const ShareLessonLabel = styled.span`
  display: inline-grid;
  justify-items: center;
  align-items: center;
  text-align: center;
  line-height: 1.15;

  > span {
    grid-area: 1 / 1;
  }

  > span:first-child {
    visibility: hidden;
    white-space: nowrap;
    pointer-events: none;
  }
`;

const CourseMenuButton = styled(NavLessonButton)`
  flex: 0 0 88px;
  width: 88px;
  font-size: 0.75rem;
  line-height: 1.15;
  text-transform: uppercase;
  padding: 0.7rem 0.4rem;
`;

function simplifyChapters(entryStore) {
  if (!entryStore) return [];
  return entryStore
    .map((topicHeader) =>
      (topicHeader.entries || []).map((chapter) => {
        const lessonEntries =
          chapter.entries &&
          chapter.entries[0] &&
          chapter.entries[0].entries
            ? chapter.entries[0].entries
            : [];
        return {
          chapterNumber: chapter.topicNumber,
          chapterTitle: chapter.title,
          lessons: lessonEntries
            .filter((lesson) => lesson.sectionNumber)
            .map((lesson) => ({
              sectionNumber: lesson.sectionNumber,
              title: lesson.title,
            })),
        };
      })
    )
    .flat();
}

function findAdjacentNavTargets(
  sectionNumber,
  entryStore,
  chapterProgressData,
  courseTitleUserIsOn
) {
  const chapters = simplifyChapters(entryStore);
  const chapterIndex = chapters.findIndex((chapter) =>
    chapter.lessons.some((lesson) => lesson.sectionNumber === sectionNumber)
  );

  if (chapterIndex === -1) {
    return { previous: null, next: null };
  }

  const chapter = chapters[chapterIndex];
  const lessonIndex = chapter.lessons.findIndex(
    (lesson) => lesson.sectionNumber === sectionNumber
  );

  let previous = null;
  if (lessonIndex > 0) {
    previous = { type: "lesson", ...chapter.lessons[lessonIndex - 1] };
  } else if (chapterIndex > 0) {
    const previousChapter = chapters[chapterIndex - 1];
    const lastLesson =
      previousChapter.lessons[previousChapter.lessons.length - 1];
    if (lastLesson) {
      previous = { type: "lesson", ...lastLesson };
    }
  }

  const isLastLessonOfChapter = lessonIndex === chapter.lessons.length - 1;
  const completedChapters =
    chapterProgressData &&
    chapterProgressData.find(
      (progress) => progress.courseProgress === courseTitleUserIsOn
    )?.chapterProgress;
  const isChapterQuizCompleted =
    Array.isArray(completedChapters) &&
    completedChapters.includes(chapter.chapterNumber);

  let next = null;
  if (!isLastLessonOfChapter) {
    next = { type: "lesson", ...chapter.lessons[lessonIndex + 1] };
  } else if (!isChapterQuizCompleted) {
    next = {
      type: "quiz",
      chapterNumber: chapter.chapterNumber,
      chapterTitle: chapter.chapterTitle,
    };
  } else {
    const nextChapter = chapters[chapterIndex + 1];
    if (nextChapter && nextChapter.lessons[0]) {
      next = { type: "lesson", ...nextChapter.lessons[0] };
    }
  }

  return { previous, next };
}

export const Lesson = (props) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(-1);
  const [selectedLesson, setSelectedLesson] = useState({});
  const [isShowQuiz, setIsShowQuiz] = useState(false);
  const [completedLessons, setCompletedLessons] = useState(null);
  const [lessonNotes, setLessonNotes] = useState([]);
  const [notesPanelOpen, setNotesPanelOpen] = useState(false);
  const [composingNote, setComposingNote] = useState(null);
  const [shareJob, setShareJob] = useState(null);
  const [shareDrawerExpanded, setShareDrawerExpanded] = useState(false);
  const [shareButtonLabel, setShareButtonLabel] = useState(
    pickRandomShareButtonLabel
  );
  const shareRequestIdRef = useRef(0);
  const shareInfographicRef = useRef(null);
  const closeShareDrawerTimeoutRef = useRef(null);
  const completedLayoutRef = useRef({ sectionNumber: null, completed: false });
  const audioRef = useRef(null);
  const history = useHistory();

  let {
    courseTitleUserIsOn,
    setCourseTitleUserIsOn,
    entryStore,
    isDarkMode,
    subContainerEntries,
    setSubContainerEntries,
    subContainer,
    setSubContainer,
    navOpen,
    setNavOpen,
    progressData,
    setProgressData,
    chapterProgressData,
    setChapterProgressData,
  } = useAmazonContext();

  const {
    courseDetails,
    isReady: isCourseDetailsReady,
    error: courseDetailsError,
    retry: retryCourseDetails,
  } = useCourseDetails(props.match.params.courseTitle);
  const [lessonLoadError, setLessonLoadError] = useState(null);
  const [lessonReloadKey, setLessonReloadKey] = useState(0);

  const convertToCloudFrontUrl = (s3Url) => {
    if (!s3Url) return s3Url;
    return s3Url.replace(
      "https://hhcourses-assets.s3.us-east-2.amazonaws.com",
      "https://d10jmd9bhq0s4v.cloudfront.net"
    );
  };

  // Helper function to ensure YouTube URLs have autoplay enabled
  const ensureYouTubeAutoplay = (videoLink) => {
    if (!videoLink) return videoLink;
    
    try {
      const url = new URL(videoLink);
      
      // Check if it's a YouTube URL
      if (url.hostname.includes('youtube.com') || url.hostname.includes('youtu.be')) {
        // Set autoplay parameter to 1 (this handles all cases: missing, 0, or already 1)
        url.searchParams.set('autoplay', '1');
        return url.toString();
      }
      
      return videoLink;
    } catch (error) {
      // If URL parsing fails, try string manipulation
      if (videoLink.includes('youtube.com') || videoLink.includes('youtu.be')) {
        // Remove existing autoplay parameter if it exists (handles autoplay=0, autoplay=1, etc.)
        let cleanUrl = videoLink.replace(/[?&]autoplay=\d+/g, '');
        
        // Check if there are any remaining parameters after cleaning
        const hasOtherParams = cleanUrl.includes('?') && cleanUrl.split('?')[1].length > 0;
        const separator = hasOtherParams ? '&' : '?';
        
        // Add autoplay parameter
        return `${cleanUrl}${separator}autoplay=1`;
      }
      return videoLink;
    }
  };

  //Logic to open the Chapter Lessons Menu subentry (on the subcontainer in the side bar) related to the lesson the user is on

  console.log(entryStore);
  useEffect(() => {
    setShareButtonLabel((prev) => pickRandomShareButtonLabel(prev));
  }, [selectedLesson?.sectionNumber]);
  useEffect(() => {
    let nextSectionNumber = null;
    let nextSectionTitle = null;
    let foundCurrentSection = false;
    // Example usage:
    const currentSectionNumber = selectedLesson.sectionNumber;

    if (entryStore) {
      //Needed to wait for entryStore to load
      // Iterate through the entryStore data
      for (const topic of entryStore) {
        for (const entry of topic.entries) {
          for (const subEntry of entry.entries) {
            for (const section of subEntry.entries) {
              // Check if the section number matches the currentSectionNumber
              if (section.sectionNumber === currentSectionNumber) {
                foundCurrentSection = true;

                setSubContainerEntries(entry.entries);
                setSubContainer(true);
                break;
              }
            }
            if (foundCurrentSection) break;
          }
          if (foundCurrentSection) break;
        }
        if (foundCurrentSection) break;
      }
    }
  }, [navOpen, courseDetails, selectedLesson]);

  //End

  // Start: Function to find the next section number and title
  function findNextSection(currentSectionNumber) {
    let nextSectionNumber = null;
    let nextSectionTitle = null;
    let nextSectionImage = null;
    let foundCurrentSection = false;

    if (entryStore) {
      //Needed to wait for entryStore to load
      // Iterate through the entryStore data
      for (const topic of entryStore) {
        for (const entry of topic.entries) {
          for (const subEntry of entry.entries) {
            for (const section of subEntry.entries) {
              // Check if the section number matches the currentSectionNumber
              if (section.sectionNumber === currentSectionNumber) {
                foundCurrentSection = true;

                // Find the index of the current section in its parent entry
                const currentIndex = subEntry.entries.findIndex(
                  (item) => item.sectionNumber === currentSectionNumber
                );

                // If the current section is not the last one in the entry
                if (currentIndex !== subEntry.entries.length - 1) {
                  // Get the next section's data
                  const nextSection = subEntry.entries[currentIndex + 1];
                  nextSectionNumber = nextSection.sectionNumber;
                  nextSectionTitle = nextSection.title;
                  nextSectionImage = nextSection.sectionImage;
                }
                break;
              }
            }
            if (foundCurrentSection) break;
          }
          if (foundCurrentSection) break;
        }
        if (foundCurrentSection) break;
      }
    }

    if (nextSectionNumber === null) {
      return false;
    }

    return {
      nextSectionNumber,
      nextSectionTitle,
      nextSectionImage,
    };
  }

  // Example usage:
  const currentSectionNumber = selectedLesson.sectionNumber; // Assuming this is the last section
  const result = findNextSection(currentSectionNumber);

  var nextLessonNumber = "";
  var nextLessonTitle = "";
  var nextLessonImage = "";

  if (result === false) {
    console.log("No next section.");
    nextLessonNumber = false;
    nextLessonTitle = false;
    nextLessonImage = false;
  } else {
    console.log("Next Section Number:", result.nextSectionNumber);
    console.log("Next Section Title:", result.nextSectionTitle);
    console.log("Next Section Image:", result.nextSectionImage);
    nextLessonNumber = result.nextSectionNumber;
    nextLessonTitle = result.nextSectionTitle;
    nextLessonImage = result.nextSectionImage;
  }
  console.log(nextLessonNumber, nextLessonTitle, nextLessonImage);

  const { previous, next } = findAdjacentNavTargets(
    selectedLesson.sectionNumber,
    entryStore,
    chapterProgressData,
    courseTitleUserIsOn
  );

  const goToNavTarget = (target) => {
    if (!target) return;
    const courseTitle = props.match.params.courseTitle;
    if (target.type === "quiz") {
      history.push(`/${courseTitle}/quiz/${target.chapterNumber}`);
      return;
    }
    history.push(`/${courseTitle}/lesson/${target.sectionNumber}`);
  };

  //End: Function to find the next section number and title

  //Finding topic number and topic title for current section

  function findChapterInfo(sectionNumber, entryStore) {
    if (entryStore) {
      for (const topic of entryStore) {
        for (const entry of topic.entries) {
          for (const chapter of entry.entries) {
            for (const section of chapter.entries) {
              if (section.sectionNumber === sectionNumber) {
                return {
                  chapterTitle: chapter.title,
                  chapterNumber: chapter.topicNumber,
                };
              }
            }
          }
        }
      }
    }

    // Return null or handle not found case as needed
    return null;
  }

  const chapterInfo = findChapterInfo(selectedLesson.sectionNumber, entryStore);

  var chapterTitle = "";
  var chapterNumber = "";
  if (chapterInfo) {
    console.log("Chapter Title:", chapterInfo.chapterTitle);
    console.log("Chapter Number:", chapterInfo.chapterNumber);
    chapterTitle = chapterInfo.chapterTitle;
    chapterNumber = chapterInfo.chapterNumber;
  } else {
    console.log("Section not found.");
  }

  console.log(courseDetails);
  console.log(entryStore);
  // Router reuses this component when moving between courses, so this has to track the
  // course in the URL rather than only run on mount.
  useEffect(() => {
    setCourseTitleUserIsOn(props.match.params.courseTitle);
  }, [props.match.params.courseTitle, setCourseTitleUserIsOn]);
  console.log(courseTitleUserIsOn);
  console.log(props.match.params.courseTitle);
  console.log(courseDetails);

  //Lesson Data from BackEnd

  console.log(props.match);
  console.log(props.match.params.lessonNumber);

  let lessonNumberFromSideBar = props.match.params.lessonNumber;
  console.log(lessonNumberFromSideBar);

  useEffect(() => {
    if (!isCourseDetailsReady) return undefined;

    let cancelled = false;
    setLessonLoadError(null);

    api
      .get(
        `/${props.match.params.courseTitle}/lessonRESTCAll/${lessonNumberFromSideBar}?collectionName=${courseDetails.courseTopicsCollectionName}&modelName=${courseDetails.courseTopicsModelName}`,
        { timeout: LESSON_REQUEST_TIMEOUT }
      )
      .then((response) => {
        if (cancelled) return;
        if (!response.data?.sectionNumber) {
          setLessonLoadError("notFound");
          return;
        }
        //changefordev
        setSelectedLesson(response.data);
        // setSelectedLesson(jsonData);
        console.log(response.data);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("Error fetching lesson:", error);
        setLessonLoadError(
          error.response?.status === 404 ? "notFound" : "failed"
        );
      });

    return () => {
      cancelled = true;
    };
  }, [lessonNumberFromSideBar, courseDetails, isCourseDetailsReady, lessonReloadKey]);

  console.log(selectedLesson);

  // Find out whether the lesson is completed
  useEffect(() => {
    if (progressData) return;

    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail) {
      setCompletedLessons(new Set());
      return;
    }

    api
      .get("/fetchProgressRecord", {
        params: { userEmail },
        timeout: LESSON_REQUEST_TIMEOUT,
      })
      .then((response) => {
        setProgressData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching progress data:", error);
        setCompletedLessons(new Set());
      });
  }, [progressData, setProgressData]);

  useEffect(() => {
    if (chapterProgressData) return;

    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail) return;

    api
      .get("/fetchChapterProgressRecord", {
        params: { userEmail },
      })
      .then((response) => {
        setChapterProgressData(response.data);
      })
      .catch((error) => {
        console.error("Error fetching chapter progress data:", error);
      });
  }, [chapterProgressData, setChapterProgressData]);

  useEffect(() => {
    if (!progressData) return;

    const courseProgressData = progressData.find(
      (progress) => progress.courseProgress === courseTitleUserIsOn
    );

    console.log(courseProgressData);

    setCompletedLessons(new Set(courseProgressData?.lessonProgress || []));
    console.log(completedLessons);
  }, [selectedLesson, progressData, courseTitleUserIsOn]);

  console.log(progressData);
  console.log(courseTitleUserIsOn);

  //End

  //Examples Data from BackEnd
  const [exampleDatafromBackEnd, setExampleDatafromBackEnd] = useState([]);

  useEffect(() => {
    if (!isCourseDetailsReady) return;

    api
      .get(
        `/examplesData?collectionName=${courseDetails.courseExamplesCollectionName}&modelName=${courseDetails.courseExamplesModelName}`
      )
      .then((response) => {
        setExampleDatafromBackEnd(response.data);
        console.log(response.data);
      });
  }, [lessonNumberFromSideBar, courseDetails, isCourseDetailsReady]);

  // let { exampleDatafromBackEnd } = useAmazonContext();
  console.log(exampleDatafromBackEnd);

  const courseTitleSlug = props.match.params.courseTitle;
  const isArabicGrammar = courseTitleSlug === "BasicArabicGrammer";

  const refreshLessonNotes = () => {
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail || !courseTitleSlug || isArabicGrammar) {
      setLessonNotes([]);
      return Promise.resolve();
    }
    return api
      .get("/userLessonNotes", {
        params: { userEmail, courseTitle: courseTitleSlug },
      })
      .then((response) => {
        setLessonNotes(
          sortNotesByLessonOrder(
            Array.isArray(response.data) ? response.data : []
          )
        );
      })
      .catch((error) => {
        console.error("Error fetching lesson notes:", error);
        setLessonNotes([]);
      });
  };

  useEffect(() => {
    refreshLessonNotes();
  }, [courseTitleSlug]);

  const persistLessonNote = async ({
    animationSectionId,
    sectionNumber,
    note,
  }) => {
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail || !courseTitleSlug) return;

    const existing = findNote(lessonNotes, sectionNumber, animationSectionId);
    const nextNote =
      typeof note === "string" ? note : existing ? existing.note || "" : "";

    setLessonNotes((prev) => {
      const withoutCurrent = prev.filter(
        (item) =>
          !(
            item.sectionNumber === sectionNumber &&
            item.animationSectionId === animationSectionId
          )
      );
      const animationSectionIndex = (
        selectedLesson.lessonAnimation || []
      ).findIndex(
        (section, index) =>
          getAnimationSectionId(
            section,
            selectedLesson.sectionNumber,
            index
          ) === animationSectionId
      );
      return sortNotesByLessonOrder([
        {
          ...(existing || {}),
          sectionNumber,
          animationSectionId,
          animationSectionIndex:
            animationSectionIndex >= 0
              ? animationSectionIndex
              : existing?.animationSectionIndex,
          note: nextNote,
          courseTitle: courseTitleSlug,
        },
        ...withoutCurrent,
      ]);
    });

    try {
      await api.put("/userLessonNotes", {
        userEmail,
        courseTitle: courseTitleSlug,
        sectionNumber,
        animationSectionId,
        note: nextNote,
      });
      await refreshLessonNotes();
    } catch (error) {
      console.error("Error saving lesson note:", error);
      refreshLessonNotes();
    }
  };

  const handleToggleHighlight = (animationSectionId) => {
    const sectionNumber = selectedLesson.sectionNumber;
    const existing = findNote(lessonNotes, sectionNumber, animationSectionId);
    if (existing) {
      handleDeleteNote(existing);
      return;
    }
    persistLessonNote({
      animationSectionId,
      sectionNumber,
      note: "",
    });
  };

  const handleOpenNote = (animationSectionId, existingNote) => {
    setComposingNote({
      animationSectionId,
      sectionNumber: selectedLesson.sectionNumber,
      note: existingNote || "",
    });
  };

  const startShareGeneration = () => {
    const shareMeta = {
      chapterTitle,
      lessonTitle: selectedLesson.sectionTitle,
      courseCardTitle:
        courseDetails?.courseCardTitle || courseDetails?.courseTitle || "",
      courseAuthor: courseDetails?.courseAuthor || "",
    };
    const hasContent = (selectedLesson.lessonAnimation || []).some(
      sectionHasShareableContent
    );
    const openDrawer = () => {
      if (closeShareDrawerTimeoutRef.current) {
        clearTimeout(closeShareDrawerTimeoutRef.current);
        closeShareDrawerTimeoutRef.current = null;
      }
      setShareDrawerExpanded(false);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setShareDrawerExpanded(true);
        });
      });
    };

    if (!hasContent) {
      setShareJob({
        requestId: Date.now(),
        status: "error",
        ...shareMeta,
        aspectRatio: "auto",
        imageBase64: null,
        mimeType: null,
        error: "This lesson has no content to share yet.",
      });
      openDrawer();
      return;
    }

    const requestId = Date.now();
    shareRequestIdRef.current = requestId;
    setShareJob({
      requestId,
      status: "generating",
      ...shareMeta,
      snapshot: {
        courseTitle: courseTitleSlug,
        sectionNumber: selectedLesson.sectionNumber,
      },
      aspectRatio: "auto",
      imageBase64: null,
      mimeType: null,
      error: null,
    });
    openDrawer();
  };

  useEffect(() => {
    if (shareJob?.status !== "generating") return undefined;
    const requestId = shareJob.requestId;
    let cancelled = false;

    const capture = async () => {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));
      const node = shareInfographicRef.current;
      if (!node) {
        throw new Error("Could not build the poster");
      }
      const dataUrl = await capturePosterPng(node, {
        backgroundColor: "#fcf5e5",
      });
      if (cancelled || shareRequestIdRef.current !== requestId) return;
      const imageBase64 = String(dataUrl).split(",")[1] || "";
      setShareJob((prev) =>
        prev && prev.requestId === requestId
          ? {
              ...prev,
              status: "ready",
              imageBase64,
              mimeType: "image/png",
            }
          : prev
      );
    };

    capture().catch((error) => {
      if (cancelled || shareRequestIdRef.current !== requestId) return;
      setShareJob((prev) =>
        prev && prev.requestId === requestId
          ? {
              ...prev,
              status: "error",
              error: error.message || "Could not generate the poster",
            }
          : prev
      );
    });

    return () => {
      cancelled = true;
    };
  }, [shareJob?.status, shareJob?.requestId]);

  const handleShareLesson = async () => {
    if (isArabicGrammar) {
      const shared = await shareTextOnly(
        buildGrammarShareLabels({
          chapterTitle,
          lessonTitle: selectedLesson.sectionTitle,
        })
      );
      if (shared) {
        handleLessonShared({
          snapshot: {
            courseTitle: courseTitleSlug,
            sectionNumber: selectedLesson.sectionNumber,
          },
        });
      }
      return;
    }
    startShareGeneration();
  };

  const renderLessonTitle = () => (
    <div className="LessonTitleContainer">
      <span className="LessonNumber">
        Lesson {selectedLesson.sectionNumber}
      </span>{" "}
      <br />
      <span className="LessonTitle">{selectedLesson.sectionTitle}</span>
    </div>
  );

  const handleCloseShareDrawer = () => {
    setShareDrawerExpanded(false);
    if (closeShareDrawerTimeoutRef.current) {
      clearTimeout(closeShareDrawerTimeoutRef.current);
    }
    closeShareDrawerTimeoutRef.current = setTimeout(() => {
      setShareJob(null);
      closeShareDrawerTimeoutRef.current = null;
    }, 360);
  };

  const handleLessonShared = (job) => {
    const userEmail = localStorage.getItem("userEmail");
    if (!userEmail || !job?.snapshot) return;
    api
      .post("/recordLessonShare", {
        userEmail,
        courseTitle: job.snapshot.courseTitle,
        sectionNumber: job.snapshot.sectionNumber,
      })
      .catch((error) => {
        console.error("Error recording lesson share:", error);
      });
  };

  const handleSaveComposedNote = (noteText) => {
    if (!composingNote) return;
    const existing = findNote(
      lessonNotes,
      composingNote.sectionNumber,
      composingNote.animationSectionId
    );
    if (!String(noteText || "").trim()) {
      if (existing) {
        handleDeleteNote(existing);
        return;
      }
      setComposingNote(null);
      return;
    }
    persistLessonNote({
      animationSectionId: composingNote.animationSectionId,
      sectionNumber: composingNote.sectionNumber,
      note: noteText,
    });
    setComposingNote(null);
  };

  const handleDeleteNote = async (item) => {
    const userEmail = localStorage.getItem("userEmail");
    if (!item || !userEmail) return;
    setLessonNotes((prev) =>
      prev.filter(
        (note) =>
          !(
            note.sectionNumber === item.sectionNumber &&
            note.animationSectionId === item.animationSectionId
          )
      )
    );
    setComposingNote(null);
    try {
      await api.delete("/userLessonNotes", {
        params: {
          userEmail,
          courseTitle: courseTitleSlug,
          sectionNumber: item.sectionNumber,
          animationSectionId: item.animationSectionId,
        },
      });
      await refreshLessonNotes();
    } catch (error) {
      console.error("Error deleting lesson note:", error);
      refreshLessonNotes();
    }
  };

  const handleJumpToNote = (item) => {
    if (!item || item.missing) return;
    setNotesPanelOpen(false);
    if (item.sectionNumber === selectedLesson.sectionNumber) {
      const jumpIndex = (selectedLesson.lessonAnimation || []).findIndex(
        (section, index) =>
          getAnimationSectionId(
            section,
            selectedLesson.sectionNumber,
            index
          ) === item.animationSectionId
      );
      if (
        jumpIndex >= 0 &&
        !completedLessons.has(selectedLesson.sectionNumber)
      ) {
        setCurrentSectionIndex((prev) => Math.max(prev, jumpIndex));
      }
      setTimeout(() => scrollToAnimationSection(item.animationSectionId), 50);
      return;
    }
    history.push(
      `/${courseTitleSlug}/lesson/${item.sectionNumber}?sectionId=${encodeURIComponent(
        item.animationSectionId
      )}`
    );
  };

  const wrapLessonSection = (section, index, inner) => {
    const sectionKey = getAnimationSectionId(
      section,
      selectedLesson.sectionNumber,
      index
    );
    if (isArabicGrammar) {
      return <React.Fragment key={sectionKey}>{inner}</React.Fragment>;
    }
    return (
      <HighlightableSection
        key={sectionKey}
        section={section}
        sectionNumber={selectedLesson.sectionNumber}
        index={index}
        note={findNote(
          lessonNotes,
          selectedLesson.sectionNumber,
          sectionKey
        )}
        isDarkMode={isDarkMode}
        onToggleHighlight={handleToggleHighlight}
        onOpenNote={handleOpenNote}
      >
        {inner}
      </HighlightableSection>
    );
  };

  const notesChrome = (
    <>
      <LessonMainSideNav
        onOpenNotebook={
          isArabicGrammar ? undefined : () => setNotesPanelOpen(true)
        }
        notesCount={isArabicGrammar ? 0 : lessonNotes.length}
      />
      {!isArabicGrammar && (
        <>
          <LessonNotesPanel
            isOpen={notesPanelOpen}
            isDarkMode={isDarkMode}
            notes={lessonNotes}
            onClose={() => setNotesPanelOpen(false)}
            onJump={handleJumpToNote}
            onDelete={handleDeleteNote}
          />
          <NoteComposer
            isOpen={!!composingNote}
            isDarkMode={isDarkMode}
            initialNote={composingNote?.note || ""}
            onSave={handleSaveComposedNote}
            onCancel={() => setComposingNote(null)}
            onDelete={() => handleDeleteNote(composingNote)}
          />
        </>
      )}
      <ShareSectionDrawer
        job={shareJob}
        expanded={shareDrawerExpanded}
        isDarkMode={isDarkMode}
        onClose={handleCloseShareDrawer}
        onRetry={() => shareJob && startShareGeneration()}
        onShared={handleLessonShared}
      />
      {shareJob?.status === "generating" && (
        <div className="share-infographic-capture" aria-hidden="true">
          <LessonShareInfographic
            ref={shareInfographicRef}
            lesson={selectedLesson}
            chapterTitle={shareJob.chapterTitle}
            lessonTitle={shareJob.lessonTitle}
            courseCardTitle={shareJob.courseCardTitle}
            courseAuthor={shareJob.courseAuthor}
            convertUrl={convertToCloudFrontUrl}
          />
        </div>
      )}
    </>
  );

  const handleNextSection = () => {
    // Increment the section index
    setCurrentSectionIndex((prevIndex) => prevIndex + 1);
  };

  const handleShowLessonExample = () => {
    setIsShowQuiz(true);
  };

  useEffect(() => {
    if (isShowQuiz) {
      // Scroll to the LessonExample component when isShowQuiz becomes true
      const lessonExampleElement = document.getElementById("lessonExample");
      if (lessonExampleElement) {
        const rect = lessonExampleElement.getBoundingClientRect();
        const offset = rect.top + window.scrollY - 80; // Adjusted offset to leave 100px space above
        window.scrollTo({
          top: offset,
          behavior: "smooth",
        });
      }
    }
  }, [isShowQuiz]);

  const isMobile = window.innerWidth <= 768; // You can adjust the breakpoint (768) as needed

  //Scroll upon click current section is at the 30% from top of screen
  useEffect(() => {
    if (currentSectionIndex >= 0 && selectedLesson.lessonAnimation) {
      const currentSectionData =
        selectedLesson.lessonAnimation[currentSectionIndex];
      if (!currentSectionData) return;
      const animationSectionId = getAnimationSectionId(
        currentSectionData,
        selectedLesson.sectionNumber,
        currentSectionIndex
      );
      scrollToAnimationSection(animationSectionId);
    }
  }, [currentSectionIndex]);

  //Reset current index when lesson changes, or jump to a notebook section
  useEffect(() => {
    setIsShowQuiz(false);
    const params = new URLSearchParams(props.location?.search || "");
    const sectionId = params.get("sectionId");
    if (sectionId && selectedLesson.lessonAnimation) {
      const jumpIndex = selectedLesson.lessonAnimation.findIndex(
        (section, index) =>
          getAnimationSectionId(
            section,
            selectedLesson.sectionNumber,
            index
          ) === sectionId
      );
      if (jumpIndex >= 0) {
        setCurrentSectionIndex(jumpIndex);
        const timeoutId = setTimeout(() => {
          scrollToAnimationSection(sectionId);
        }, 150);
        return () => clearTimeout(timeoutId);
      }
    }
    setCurrentSectionIndex(-1);
  }, [lessonNumberFromSideBar, selectedLesson, props.location?.search]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [lessonNumberFromSideBar]);

  // Randomiser function to choose an example for this Lesson

  // const chooseRandomExample = (sectionNumber) => {
  //   let allSectionExamples =
  //     exampleDatafromBackEnd.length !== 0 &&
  //     exampleDatafromBackEnd.filter(
  //       //list of ALL examples for a particular section in the database
  //       (example) => example.sectionNumber == sectionNumber
  //     );
  //   const random = Math.floor(Math.random() * allSectionExamples.length);
  //   let selectedExample = allSectionExamples[random];
  //   return selectedExample;
  // };

  console.log(currentSectionIndex);

  const playAyahAudio = (ayahReference) => {
    // Parse surah and ayah numbers from format "[Surat Al Baqarah 2:183]"
    const match = ayahReference.match(/\[.*?(\d+):(\d+)\]/);
    if (match) {
      const [_, surahNo, ayahNo] = match;
      const audioUrl = `https://quranaudio.pages.dev/2/${surahNo}_${ayahNo}.mp3`;

      if (audioRef.current) {
        audioRef.current.src = audioUrl;
        audioRef.current.play();
      }
    }
  };

  // Add this useEffect near other useEffect hooks
  useEffect(() => {
    if (currentSectionIndex >= 0 && selectedLesson.lessonAnimation) {
      const currentSection =
        selectedLesson.lessonAnimation[currentSectionIndex];
      if (currentSection?.name?.includes("Ayah")) {
        // Check if any previous section also has "Ayah" in its name
        const hasPreviousAyah = selectedLesson.lessonAnimation
          .slice(0, currentSectionIndex)
          .some(section => section?.name?.includes("Ayah"));

        // Only play audio if this is the first Ayah section
        if (!hasPreviousAyah) {
          const lastSubSection = currentSection.subSections.slice(-1)[0];
          if (lastSubSection?.content) {
            setTimeout(() => playAyahAudio(lastSubSection.content), 0);
          }
        }
      }
    }
  }, [currentSectionIndex]);

  const loadError = courseDetailsError || lessonLoadError;
  if (loadError) {
    const isNotFound = loadError === "notFound";
    const retryLoad = () => {
      if (courseDetailsError) {
        retryCourseDetails();
      } else {
        setLessonReloadKey((key) => key + 1);
      }
    };
    return (
      <LessonLoadError $isDarkMode={isDarkMode}>
        <p>
          {isNotFound
            ? "We couldn't find this lesson. It may have moved."
            : "This lesson is taking too long to load. Please check your connection and try again."}
        </p>
        <div className="lessonLoadErrorActions">
          {!isNotFound && <button onClick={retryLoad}>Try again</button>}
          <button
            className="secondary"
            onClick={() =>
              history.push(
                courseDetailsError === "notFound"
                  ? "/home"
                  : `/${props.match.params.courseTitle}`
              )
            }
          >
            Back to course
          </button>
        </div>
      </LessonLoadError>
    );
  }

  const isLessonReady =
    selectedLesson &&
    selectedLesson !== "undefined" &&
    selectedLesson.sectionNumber;

  if (!isLessonReady || completedLessons === null) {
    return <div>Loading</div>;
  }

  // Progress can refresh while the lesson is on screen (opening the course menu
  // refetches it), so the completed/in-progress choice is locked per lesson to
  // keep the page from swapping under the user after the lesson quiz.
  if (completedLayoutRef.current.sectionNumber !== selectedLesson.sectionNumber) {
    completedLayoutRef.current = {
      sectionNumber: selectedLesson.sectionNumber,
      completed: completedLessons.has(selectedLesson.sectionNumber),
    };
  }
  const showCompletedLayout = completedLayoutRef.current.completed;

  const renderIncompleteNavButton = () => {
    if (!selectedLesson.lessonAnimation || isShowQuiz) {
      return null;
    }

    const totalSections = selectedLesson.lessonAnimation.length;
    const isStart = currentSectionIndex === -1;
    const isContinue =
      currentSectionIndex >= 0 && currentSectionIndex < totalSections - 1;
    const isQuizStep =
      currentSectionIndex === totalSections - 1 && isShowQuiz === false;
    const progressPercent =
      totalSections > 0
        ? ((currentSectionIndex + 1) / totalSections) * 100
        : 0;
    if (isStart) {
      return (
        <LessonSolidButton
          type="button"
          onClick={handleNextSection}
          style={{ backgroundColor: "#8747d4" }}
          shadowColor="#6535a0"
        >
          START
        </LessonSolidButton>
      );
    }

    if (isContinue) {
      return (
        <LessonSolidButton
          type="button"
          $variant="continue"
          onClick={handleNextSection}
          style={{ backgroundColor: "rgb(80, 200, 120)" }}
          shadowColor="rgb(60, 150, 90)"
        >
          <ContinueProgressTrack>
            <ContinueProgressFill
              initial={{ scaleX: 0 }}
              animate={{ scaleX: progressPercent / 100 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
            />
          </ContinueProgressTrack>
          CONTINUE
        </LessonSolidButton>
      );
    }

    if (isQuizStep) {
      return (
        <LessonSolidButton
          type="button"
          onClick={handleShowLessonExample}
          style={{ backgroundColor: "#8747d4" }}
          shadowColor="#6535a0"
        >
          {isArabicGrammar ? (
            <>
              WATCH THE VIDEO <br />
              THEN PRESS HERE
            </>
          ) : (
            "QUIZ"
          )}
        </LessonSolidButton>
      );
    }

    return null;
  };

  const renderLessonActionBar = ({ showNext = true, sticky = true } = {}) => {
    const previousLabel =
      previous && previous.type === "lesson"
        ? previous.title
        : "Previous lesson";
    const nextLabel = next
      ? next.type === "quiz"
        ? "Chapter Quiz"
        : next.title
      : "Next lesson";

    return (
      <CompletedStickyBar $sticky={sticky}>
        <NavArrowButton
          type="button"
          disabled={!previous}
          aria-label={previousLabel}
          onClick={() => goToNavTarget(previous)}
        >
          <ChevronLeft size={28} strokeWidth={2.5} />
        </NavArrowButton>
        {showNext ? (
          <NavArrowButton
            type="button"
            disabled={!next}
            aria-label={nextLabel}
            onClick={() => goToNavTarget(next)}
          >
            <ChevronRight size={28} strokeWidth={2.5} />
          </NavArrowButton>
        ) : null}
        <ShareLessonButton
          type="button"
          aria-label="Share this lesson"
          onClick={handleShareLesson}
        >
          <Share2 size={18} strokeWidth={2.5} />
          <ShareLessonLabel>
            <span aria-hidden="true">{SHARE_BUTTON_LABEL_SIZER}</span>
            <span>{shareButtonLabel}</span>
          </ShareLessonLabel>
        </ShareLessonButton>
        <CourseMenuButton type="button" onClick={() => setNavOpen(true)}>
          COURSE
          <br />
          MENU
        </CourseMenuButton>
      </CompletedStickyBar>
    );
  };

  const renderCompletedNavBar = () =>
    renderLessonActionBar({ showNext: true, sticky: true });

  if (showCompletedLayout) {
    return (
      <div>
        {notesChrome}
        <div className="Lesson">
          <div className="LessonContainer">
            {renderLessonTitle()}

            {selectedLesson.lessonAnimation &&
              selectedLesson.lessonAnimation.map((section, index) =>
                section.className == "multiSection" ? wrapLessonSection(section, index, (
                  <motion.div
                    className={section.className}
                    style={section.style}
                  >
                    {section.subSections.map((subSection, subSectionIndex) => (
                      <motion.div
                        className={subSection.className}
                        style={subSection.style}
                        key={subSectionIndex}
                      >
                        {subSection.miniSections.map(
                          (miniSection, miniSectionIndex) =>
                            miniSection.className === "LottieAnimation" ? (
                              <LottieAnimation
                                key={miniSectionIndex}
                                lottieFile={convertToCloudFrontUrl(
                                  miniSection.lottieFilePath
                                )}
                                className={miniSection.className}
                                style={miniSection.style}
                                speed={miniSection.speed}
                                loop={miniSection.loop}
                              ></LottieAnimation>
                            ) : miniSection.className ===
                              "sectionDescription image" ? (
                              <motion.div
                                key={miniSectionIndex}
                                className={`${miniSection.className} ${
                                  isDarkMode
                                    ? "darkThemeFont"
                                    : "lightThemeFont"
                                }`}
                                style={miniSection.style}
                              >
                                <div style={miniSection.contentStyle}>
                                  <img
                                    src={convertToCloudFrontUrl(
                                      miniSection.imageFilePath
                                    )}
                                    alt="noImage"
                                  ></img>
                                </div>
                                {/* Use subSection.content here */}
                              </motion.div>
                            ) : (
                              <motion.div
                                key={miniSectionIndex}
                                className={`${miniSection.className} ${
                                  isDarkMode
                                    ? "darkThemeFont"
                                    : "lightThemeFont"
                                }`}
                                style={miniSection.style}
                              >
                                <div style={{
                                  ...miniSection.contentStyle,
                                  ...(section.name?.includes("Ayah") && miniSectionIndex === 0
                                    ? { fontFamily: "'Me Quran', serif", direction: "rtl" }
                                    : {})
                                }}>
                                  {ReactHtmlParser(
                                    section.name?.includes("Ayah") && miniSectionIndex === 0
                                      ? normalizeMeQuranArabic(miniSection.content)
                                      : miniSection.content
                                  )}{" "}
                                </div>
                              </motion.div>
                            )
                        )}
                      </motion.div>
                    ))}
                  </motion.div>
                )) : wrapLessonSection(section, index, (
                  <motion.div
                    className={section.className}
                    style={section.style}
                  >
                    {section.subSections.map((subSection, subSectionIndex) =>
                      subSection.className == "YouTubeVideo" ? (
                        <div style={subSection.style}>
                          <div className="ratio ratio-16x9">
                            <iframe
                              src={ensureYouTubeAutoplay(subSection.videoLink)}
                              title="YouTube video"
                              allowFullScreen
                              style={{ width: "100%", height: "100%" }}
                              allow="autoplay; encrypted-media"
                            ></iframe>
                          </div>
                        </div>
                      ) : subSection.className == "LottieAnimation" ? (
                        <LottieAnimation
                          key={subSectionIndex}
                          lottieFile={convertToCloudFrontUrl(
                            subSection.lottieFilePath
                          )}
                          className={subSection.className}
                          style={subSection.style}
                          speed={subSection.speed}
                          loop={subSection.loop}
                        ></LottieAnimation>
                      ) : subSection.className ===
                        "sectionDescription image" ? (
                        <motion.div
                          key={subSectionIndex}
                          className={`${subSection.className} ${
                            isDarkMode ? "darkThemeFont" : "lightThemeFont"
                          }`}
                          style={subSection.style}
                        >
                          <div style={subSection.contentStyle}>
                            <img
                              src={convertToCloudFrontUrl(
                                subSection.imageFilePath
                              )}
                              alt="noImage"
                            ></img>
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key={subSectionIndex}
                          className={`${subSection.className} ${
                            isDarkMode ? "darkThemeFont" : "lightThemeFont"
                          }`}
                          style={subSection.style}
                        >
                          <div style={{
                            ...subSection.contentStyle,
                            ...(section.name?.includes("Ayah") && subSectionIndex === 0
                              ? { fontFamily: "'Me Quran', serif", direction: "rtl" }
                              : {})
                          }}>
                            {ReactHtmlParser(
                              section.name?.includes("Ayah") && subSectionIndex === 0
                                ? normalizeMeQuranArabic(subSection.content)
                                : subSection.content
                            )}{" "}
                          </div>
                        </motion.div>
                      )
                    )}
                  </motion.div>
                ))
              )}

            <div style={{ minHeight: "120px", zIndex: 999 }}></div>
          </div>
        </div>
        {renderCompletedNavBar()}
      </div>
    );
  }

  //Return this if the lesson has not been completed:

  return (
    <div>
      {notesChrome}
      <div className="Lesson">
        <div className="LessonContainer">
          {renderLessonTitle()}

          {selectedLesson.lessonAnimation &&
            selectedLesson.lessonAnimation.map(
              (section, index) =>
                index <= currentSectionIndex &&
                (section.className == "multiSection" ? wrapLessonSection(section, index, (
                  <motion.div
                    className={section.className}
                    style={section.style}
                    initial={section.initial}
                    animate={section.animate}
                    exit={section.exit}
                    transition={section.transition}
                  >
                    {section.subSections.map((subSection, subSectionIndex) => (
                      <motion.div
                        id={`subSection-${index}`}
                        className={subSection.className}
                        style={subSection.style}
                        initial={subSection.initial}
                        animate={subSection.animate}
                        exit={subSection.exit}
                        transition={subSection.transition}
                        key={index}
                      >
                        {subSection.miniSections.map(
                          (miniSection, miniSectionIndex) =>
                            miniSection.className === "LottieAnimation" ? (
                              <LottieAnimation
                                key={miniSectionIndex}
                                lottieFile={convertToCloudFrontUrl(
                                  miniSection.lottieFilePath
                                )}
                                className={miniSection.className}
                                style={miniSection.style}
                                initial={miniSection.initial}
                                animate={miniSection.animate}
                                exit={miniSection.exit}
                                transition={miniSection.transition}
                                speed={miniSection.speed}
                                loop={miniSection.loop}
                              ></LottieAnimation>
                            ) : miniSection.className ===
                              "sectionDescription image" ? (
                              <motion.div
                                key={miniSectionIndex}
                                className={`${miniSection.className} ${
                                  isDarkMode
                                    ? "darkThemeFont"
                                    : "lightThemeFont"
                                }`}
                                style={miniSection.style}
                                initial={miniSection.initial}
                                animate={miniSection.animate}
                                exit={miniSection.exit}
                                transition={miniSection.transition}
                              >
                                <div style={miniSection.contentStyle}>
                                  <img
                                    src={convertToCloudFrontUrl(
                                      miniSection.imageFilePath
                                    )}
                                    alt="noImage"
                                  ></img>
                                </div>
                                {/* Use subSection.content here */}
                              </motion.div>
                            ) : (
                              <motion.div
                                key={miniSectionIndex}
                                className={`${miniSection.className} ${
                                  isDarkMode
                                    ? "darkThemeFont"
                                    : "lightThemeFont"
                                }`}
                                style={miniSection.style}
                                initial={miniSection.initial}
                                animate={miniSection.animate}
                                exit={miniSection.exit}
                                transition={miniSection.transition}
                              >
                                <div style={{
                                  ...miniSection.contentStyle,
                                  ...(section.name?.includes("Ayah") && miniSectionIndex === 0
                                    ? { fontFamily: "'Me Quran', serif", direction: "rtl" }
                                    : {})
                                }}>
                                  {ReactHtmlParser(
                                    section.name?.includes("Ayah") && miniSectionIndex === 0
                                      ? normalizeMeQuranArabic(miniSection.content)
                                      : miniSection.content
                                  )}{" "}
                                </div>
                              </motion.div>
                            )
                        )}
                      </motion.div>
                    ))}
                  </motion.div>
                )) : wrapLessonSection(section, index, (
                  <motion.div
                    className={section.className}
                    style={section.style}
                    initial={section.initial}
                    animate={section.animate}
                    exit={section.exit}
                    transition={section.transition}
                  >
                    <audio ref={audioRef} />

                    {section.subSections.map((subSection, subSectionIndex) =>
                      subSection.className == "YouTubeVideo" ? (
                        <div style={subSection.style}>
                          <div className="ratio ratio-16x9">
                            <iframe
                              src={ensureYouTubeAutoplay(subSection.videoLink)}
                              title="YouTube video"
                              allowFullScreen
                              style={{ width: "100%", height: "100%" }}
                              allow="autoplay; encrypted-media"
                            ></iframe>
                          </div>
                        </div>
                      ) : subSection.className == "LottieAnimation" ? (
                        <LottieAnimation
                          key={subSectionIndex}
                          lottieFile={convertToCloudFrontUrl(
                            subSection.lottieFilePath
                          )}
                          className={subSection.className}
                          style={subSection.style}
                          initial={subSection.initial}
                          animate={subSection.animate}
                          exit={subSection.exit}
                          transition={subSection.transition}
                          speed={subSection.speed}
                          loop={subSection.loop}
                        ></LottieAnimation>
                      ) : subSection.className ===
                        "sectionDescription image" ? (
                        <motion.div
                          key={subSectionIndex}
                          className={`${subSection.className} ${
                            isDarkMode ? "darkThemeFont" : "lightThemeFont"
                          }`}
                          style={subSection.style}
                          initial={subSection.initial}
                          animate={subSection.animate}
                          exit={subSection.exit}
                          transition={subSection.transition}
                        >
                          <div style={subSection.contentStyle}>
                            <img
                              src={convertToCloudFrontUrl(
                                subSection.imageFilePath
                              )}
                              alt="noImage"
                            ></img>
                          </div>
                        </motion.div>
                      ) : (
                        <motion.div
                          key={subSectionIndex}
                          className={`${subSection.className} ${
                            isDarkMode ? "darkThemeFont" : "lightThemeFont"
                          }`}
                          style={subSection.style}
                          initial={subSection.initial}
                          animate={subSection.animate}
                          exit={subSection.exit}
                          transition={subSection.transition}
                        >
                          <div style={{
                            ...subSection.contentStyle,
                            ...(section.name?.includes("Ayah") && subSectionIndex === 0
                              ? { fontFamily: "'Me Quran', serif", direction: "rtl" }
                              : {})
                          }}>
                            {ReactHtmlParser(
                              section.name?.includes("Ayah") && subSectionIndex === 0
                                ? normalizeMeQuranArabic(subSection.content)
                                : subSection.content
                            )}{" "}
                          </div>
                        </motion.div>
                      )
                    )}
                  </motion.div>
                )))
            )}

          {selectedLesson.lessonAnimation && isShowQuiz && (
            <motion.div
              id="lessonExample"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: "spring",
                stiffness: 260,
                damping: 10,
              }}
            >
              <LessonExample
                currentSectionNumber={currentSectionNumber}
                nextLessonNumber={nextLessonNumber}
                nextLessonTitle={nextLessonTitle}
                nextLessonImage={nextLessonImage}
                chapterNumber={chapterNumber}
                chapterTitle={chapterTitle}
                lessonNumber={selectedLesson.sectionNumber}
                exampleDatafromBackEnd={exampleDatafromBackEnd}
                postQuizNav={renderLessonActionBar({
                  showNext: false,
                  sticky: false,
                })}
              ></LessonExample>
            </motion.div>
          )}
          <div style={{ minHeight: "40vh", zIndex: 999 }}></div>
        </div>
        {renderIncompleteNavButton()}
      </div>
    </div>
  );
};

export default Lesson;
